import React, { useMemo, useRef, useEffect, useState } from 'react';
import GridLayout, { type LayoutItem } from 'react-grid-layout';
import { Link } from 'react-router-dom';
import { Card, LoadingSpinner } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { createWidgetElement } from '../widgetRegistry';
import {
  SavedReportWidget,
  EFORM_SAVED_REPORT_WIDGET_ID,
} from '../../e-forms/SavedReportWidget';
import { useEFormAppPage } from '../../e-forms/useData';

function parsePageId(settings?: Record<string, unknown>, pageIdProp?: unknown): number | null {
  const raw = pageIdProp ?? settings?.pageId;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(480);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, entry.contentRect.width)));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/**
 * Host for a Form Studio “Dashboard widget” (EFormAppPage) on the central dashboard.
 */
const EformsFormWidgetHost: React.FC<{
  pageId?: number | string;
  settings?: Record<string, unknown>;
  embedded?: boolean;
  isEditing?: boolean;
}> = ({ pageId: pageIdProp, settings, isEditing }) => {
  const { colors } = useTheme();
  const pageId = parsePageId(settings, pageIdProp);
  const { data: page, isLoading } = useEFormAppPage(pageId);
  const { ref, width } = useContainerWidth();

  const widgets = useMemo(() => page?.layoutJson?.widgets ?? [], [page?.layoutJson?.widgets]);
  const pageDefinitionId = page?.definitionId ?? null;
  const tileSettings = (settings?: Record<string, unknown>) => ({
    ...(settings ?? {}),
    ...(pageDefinitionId ? { definitionId: pageDefinitionId } : {}),
  });
  const layout: LayoutItem[] = useMemo(
    () => widgets.map((w) => ({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h })),
    [widgets],
  );

  if (!pageId) {
    return (
      <div className={cn('flex h-full items-center justify-center p-4 text-sm', colors.textMuted)}>
        Choose a Form Studio dashboard widget in settings.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!page) {
    return (
      <div className={cn('flex h-full items-center justify-center p-4 text-sm', colors.textMuted)}>
        This dashboard widget was deleted or is unavailable.
      </div>
    );
  }

  if (!widgets.length) {
    return (
      <div className={cn('flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-sm', colors.textMuted)}>
        <p>“{page.name}” has no tiles yet.</p>
        {isEditing ? (
          <Link className="text-blue-600 hover:underline dark:text-blue-400" to={`/e-forms/apps/${page.id}/edit`}>
            Edit in Form Studio
          </Link>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {page.description ? (
        <p className={cn('shrink-0 truncate px-1 pb-2 text-xs', colors.textMuted)} title={page.description}>
          {page.description}
        </p>
      ) : null}
      <div ref={ref} className="min-h-0 flex-1 overflow-auto">
        <GridLayout
          width={width}
          layout={layout}
          gridConfig={{ cols: 12, rowHeight: 72, margin: [8, 8], containerPadding: [0, 0] }}
          dragConfig={{ enabled: false }}
          resizeConfig={{ enabled: false }}
        >
          {widgets.map((widget) => (
            <Card key={widget.id} className="overflow-auto p-2">
              {widget.type === EFORM_SAVED_REPORT_WIDGET_ID ? (
                <SavedReportWidget settings={tileSettings(widget.settings)} />
              ) : (
                createWidgetElement(widget.type, tileSettings(widget.settings))
              )}
            </Card>
          ))}
        </GridLayout>
      </div>
    </div>
  );
};

export default EformsFormWidgetHost;
